import { useTraces } from "@/lib/hooks/useTraces";
import { groupSpansByTrace } from "@/utils/trace-utils";
import { useMemo } from "react";
import type { DockmasterSpan } from "../../../../schemas/codegen";
import type { EnhancedTrace } from "../../CodeGen/Chat/components/traces/types";

export function useCompactTracesData(projectId: string) {
  // Fetch traces data
  const { data: tracesData, isLoading, error } = useTraces(projectId);

  // Process traces into enhanced format and extract compact display data
  const { enhancedTraces, compactTraces } = useMemo(() => {
    const rawTraces = tracesData?.traces ?? [];

    // Process traces using utility functions (same as ProjectObservabilityTab)
    const enhancedTraces: EnhancedTrace[] = rawTraces.map((trace) => {
      if (!trace.spans || trace.spans.length === 0) {
        return {
          traceId: trace.traceId,
          startTime: 0,
          endTime: 0,
          duration: 0,
          spanCount: 0,
          hasErrors: false,
          spans: [],
        };
      }

      // Use utility to process the trace (cast spans to correct type)
      const [processedTrace] = groupSpansByTrace(
        trace.spans as DockmasterSpan[],
      );
      return (
        processedTrace || {
          traceId: trace.traceId,
          startTime: 0,
          endTime: 0,
          duration: 0,
          spanCount: 0,
          hasErrors: false,
          spans: [],
        }
      );
    });

    // Extract compact display data
    const compactTraces = enhancedTraces
      .slice(0, 10) // Show max 10 recent traces
      .map((trace) => {
        const rootSpan = trace.spans?.find((span) => span.isRootSpan);
        const firstSpan = trace.spans?.[0];
        const displaySpan = rootSpan || firstSpan;

        // Extract request (method/operation)
        const request =
          displaySpan?.mcpMethodName ||
          displaySpan?.operationName ||
          "Unknown Request";

        // Extract target (tool/resource)
        const target =
          displaySpan?.mcpToolName ||
          displaySpan?.mcpMethodName ||
          displaySpan?.attributes?.["mcp.tool"] ||
          displaySpan?.attributes?.["mcp.resource"] ||
          null;

        // Format timestamp
        const timestamp = trace.startTime
          ? new Date(trace.startTime / 1000000).toLocaleTimeString("en-US", {
              hour12: false,
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            })
          : "--:--:--";

        return {
          traceId: trace.traceId,
          hasErrors: trace.hasErrors,
          request,
          target,
          timestamp,
          duration: trace.duration,
          spanCount: trace.spanCount,
          // Combined request/target for minimal space
          requestTarget: target ? `${request} → ${target}` : request,
        };
      });

    return { enhancedTraces, compactTraces };
  }, [tracesData]);

  return {
    compactTraces,
    enhancedTraces,
    totalTraces: tracesData?.traces?.length || 0,
    isLoading,
    error,
  };
}
